"use client";

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

// Same outline for the gray background star and the yellow filled one.
const STAR_PATH = "M9.60428 1.91249C9.6408 1.83871 9.69722 1.7766 9.76716 1.73318C9.8371 1.68976 9.91779 1.66675 10.0001 1.66675C10.0824 1.66675 10.1631 1.68976 10.2331 1.73318C10.303 1.7766 10.3594 1.83871 10.396 1.91249L12.3209 5.81166C12.4478 6.0683 12.635 6.29033 12.8665 6.4587C13.098 6.62707 13.3669 6.73675 13.6501 6.77833L17.9551 7.40833C18.0367 7.42015 18.1133 7.45455 18.1764 7.50766C18.2394 7.56076 18.2863 7.63045 18.3118 7.70883C18.3373 7.78721 18.3403 7.87116 18.3206 7.95119C18.3009 8.03121 18.2591 8.10412 18.2001 8.16166L15.0868 11.1933C14.8815 11.3934 14.7278 11.6404 14.6391 11.913C14.5504 12.1856 14.5293 12.4757 14.5776 12.7583L15.3126 17.0417C15.327 17.1232 15.3182 17.2071 15.2872 17.2839C15.2562 17.3607 15.2042 17.4272 15.1372 17.4758C15.0702 17.5245 14.9909 17.5533 14.9083 17.5591C14.8257 17.5648 14.7432 17.5472 14.6701 17.5083L10.8218 15.485C10.5682 15.3518 10.2861 15.2823 9.9997 15.2823C9.7133 15.2823 9.43119 15.3518 9.17762 15.485L5.33012 17.5083C5.25706 17.547 5.17461 17.5644 5.09216 17.5585C5.0097 17.5527 4.93055 17.5238 4.8637 17.4752C4.79684 17.4266 4.74498 17.3601 4.714 17.2835C4.68302 17.2069 4.67416 17.1231 4.68845 17.0417L5.42262 12.7592C5.47111 12.4764 5.4501 12.1862 5.3614 11.9134C5.27269 11.6406 5.11896 11.3935 4.91345 11.1933L1.80012 8.16249C1.74061 8.10502 1.69844 8.03199 1.67842 7.95172C1.65839 7.87145 1.66131 7.78717 1.68685 7.70849C1.71238 7.6298 1.75951 7.55986 1.82285 7.50665C1.88619 7.45343 1.9632 7.41907 2.04512 7.40749L6.34928 6.77833C6.63283 6.73708 6.90211 6.62754 7.13394 6.45915C7.36577 6.29076 7.5532 6.06855 7.68012 5.81166L9.60428 1.91249Z";

const StarShape = ({ size, color }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="block"
  >
    <path
      d={STAR_PATH}
      fill={color}
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// The partial fill used to be an SVG <clipPath> whose id came from useId(). Under next/dynamic (the product page's
// _client.jsx) useId gives different ids on the server and in hydration, so the clipPath id mismatched and React
// warned "A tree hydrated but some attributes ... didn't match". The yellow star is now cropped by a box of the
// fill width instead: no id at all. The row is dir="ltr", so the fill grows from the left as before.
const Star = ({ fraction = 0, size = 30 }) => {
  const f = clamp(fraction, 0, 1);

  return (
    <span className="relative block" style={{ width: size, height: size }} aria-hidden="true">
      <StarShape size={size} color="#D1D5DB" />
      <span className="absolute inset-y-0 left-0 overflow-hidden" style={{ width: `${f * 100}%` }}>
        <StarShape size={size} color="#FACC15" />
      </span>
    </span>
  );
};

const StarRating = ({ value = 0, onChange, size = 30, max = 5, readOnly = false }) => {
  const v = clamp(value, 0, max);

  return (
    <div className="flex gap-1" dir="ltr">
      {Array.from({ length: max }, (_, i) => {
        const starValue = i + 1;
        const diff = v - i;
        const fraction = diff >= 1 ? 1 : diff >= 0.5 ? 0.5 : diff > 0 ? diff : 0;

        return (
          <button
            key={starValue}
            type="button"
            onClick={() => onChange?.(starValue)}
            disabled={readOnly}
            className="cursor-pointer p-0"
            aria-label={`Rate ${starValue} star`}
          >
            <Star fraction={fraction} size={size} />
          </button>
        );
      })}
    </div>
  );
};

export default StarRating;
